import { useState, useRef } from 'react'
import { supabase } from '../../../../lib/supabase'
import { sendReminderEmail } from '../../../../lib/emailService'
import CardDisplay from '../../../MembershipCard/components/CardDisplay'
import { toPng } from 'html-to-image'

export default function MemberDetail({ member, onClose, onRefresh, chapters }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deactivateModal, setDeactivateModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [formData, setFormData] = useState({
    full_name: member.full_name || '',
    phone: member.phone || '',
    region: member.region || '',
    gender: member.gender || '',
    date_of_birth: member.date_of_birth || '',
    chapter_id: member.chapter_id || '',
    status: member.status || 'active',
  })

  const [cardModal, setCardModal] = useState(false)
  const cardRef = useRef(null)
  const initials = member?.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase()

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value })

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const { error } = await supabase.from('supporters').update(formData).eq('id', member.id)
    if (error) { setError(`Save failed: ${error.message}`); setSaving(false); return }
    setSaving(false)
    setEditing(false)
    onRefresh()
  }

  const handleDeactivateConfirm = async () => {
    setActionLoading(true)
    const newStatus = member.status === 'active' ? 'inactive' : 'active'
    const { error } = await supabase.from('supporters').update({ status: newStatus }).eq('id', member.id)
    if (error) { setError(`Status update failed: ${error.message}`); setActionLoading(false); setDeactivateModal(false); return }
    setActionLoading(false)
    setDeactivateModal(false)
    onRefresh()
    onClose()
  }

  const handleDeleteConfirm = async () => {
    setActionLoading(true)
    await supabase.from('subscriptions').delete().eq('supporter_id', member.id)
    await supabase.from('notifications').delete().eq('user_id', member.id)
    const { error } = await supabase.from('supporters').delete().eq('id', member.id)
    if (error) { setError(`Delete failed: ${error.message}`); setActionLoading(false); setDeleteModal(false); return }
    setActionLoading(false)
    setDeleteModal(false)
    onRefresh()
    onClose()
  }

  const handleSendReminder = async () => {
    setError(null)
    setSuccess(null)
    try {
      const { data: clubData } = await supabase.from('clubs').select('name').eq('id', member.club_id).single()
      const result = await sendReminderEmail({
        fullName: member.full_name,
        email: member.email,
        membershipId: member.membership_id,
        clubName: clubData?.name || 'DSMS',
      })
      if (result.success) setSuccess('Subscription reminder sent successfully')
      else setError(`Failed to send reminder: ${result.error || 'Unknown error'}`)
    } catch (err) {
      setError(`Failed to send reminder: ${err.message}`)
    }
  }

  const handleRequestHeadshot = async () => {
    setError(null)
    setSuccess(null)
    try {
      const { error } = await supabase.from('notifications').insert({
        user_id: member.id,
        club_id: member.club_id,
        title: 'Profile Photo Required',
        message: 'Please update your profile photo with a clear headshot of your face to maintain access to your membership card privileges.',
        type: 'general',
        link: '/profile',
      })
      if (error) { setError(`Notification failed: ${error.message}`); return }
      setSuccess('Headshot request notification sent to member')
    } catch (err) {
      setError(`Failed to send notification: ${err.message}`)
    }
  }

  const [roleModal, setRoleModal] = useState(false)
  const [promoting, setPromoting] = useState(false)

  const handlePromoteConfirm = async () => {
    setPromoting(true)
    const newRole = member.role === 'supporter' ? 'chapter_coordinator' : 'supporter'
    const { error } = await supabase.from('supporters').update({ role: newRole }).eq('id', member.id)
    if (error) { setError(`Role update failed: ${error.message}`); setPromoting(false); setRoleModal(false); return }
    setPromoting(false)
    setRoleModal(false)
    onRefresh()
    onClose()
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  const details = [
    { label: 'Membership ID', value: member.membership_id },
    { label: 'Email', value: member.email },
    { label: 'Phone', value: member.phone || 'Not provided' },
    { label: 'WhatsApp', value: member.whatsapp_number || 'Not provided' },
    { label: 'Gender', value: member.gender || 'Not provided' },
    { label: 'Date of Birth', value: member.date_of_birth ? new Date(member.date_of_birth).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not provided' },
    { label: 'Region', value: member.region || 'Not provided' },
    { label: 'Chapter', value: member.chapters?.name || 'Not assigned' },
    { label: 'Status', value: member.status },
    { label: 'Member Since', value: new Date(member.joined_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) },
  ]

  const nokName = member.next_of_kin_name
  const nokPhone = member.next_of_kin_phone

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 flex justify-end">
        <div className="flex-1" onClick={onClose} />
        <div className="w-full max-w-md bg-white dark:bg-gray-950 border-l border-gray-200 dark:border-white/10 h-full overflow-y-auto">

          <div className="px-8 py-6 border-b border-gray-200 dark:border-white/10 flex justify-between items-center">
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Member Details</div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setEditing(!editing); setError(null); setSuccess(null) }}
                className={`text-xs font-bold uppercase tracking-widest transition-colors ${editing ? 'text-gray-400 hover:text-gray-900 dark:hover:text-white' : 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'}`}
              >
                {editing ? 'Cancel' : 'Edit'}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-lg">✕</button>
            </div>
          </div>

          <div className="px-8 py-6 border-b border-gray-200 dark:border-white/10 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-green-900/50 border-2 border-green-700/50 flex items-center justify-center text-white font-black text-lg shrink-0">
              {member.profile_photo ? <img src={member.profile_photo} alt="Profile" className="w-full h-full object-cover" /> : initials}
            </div>
            <div>
              <div className="font-black text-xl uppercase tracking-tight text-gray-900 dark:text-white">{member.full_name}</div>
              <div className={`text-xs font-bold uppercase tracking-widest mt-1 ${member.status === 'active' ? 'text-green-500 dark:text-green-400' : 'text-yellow-500 dark:text-yellow-400'}`}>
                {member.status}
              </div>
              {!member.profile_photo && <div className="text-xs text-yellow-500 mt-1">No profile photo uploaded</div>}
            </div>
          </div>

          {error && <div className="mx-8 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm">{error}</div>}
          {success && <div className="mx-8 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm">{success}</div>}

          {editing ? (
            <div className="px-8 py-6 flex flex-col gap-4">
              <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">Edit Member Info</div>
              <div className="flex flex-col gap-2"><label className={labelClass}>Full Name</label><input type="text" name="full_name" value={formData.full_name} onChange={handleChange} className={inputClass} /></div>
              <div className="flex flex-col gap-2"><label className={labelClass}>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} /></div>
              <div className="flex flex-col gap-2"><label className={labelClass}>Region</label><input type="text" name="region" value={formData.region} onChange={handleChange} className={inputClass} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass}>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2"><label className={labelClass}>Date of Birth</label><input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className={inputClass} /></div>
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Chapter</label>
                <select name="chapter_id" value={formData.chapter_id} onChange={handleChange} className={inputClass}>
                  <option value="">Select chapter</option>
                  {chapters?.map((chapter) => <option key={chapter.id} value={chapter.id}>{chapter.name}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex gap-3 mt-2">
                <button onClick={() => setEditing(false)} className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-8 py-6">
                <div className="flex flex-col gap-5">
                  {details.map((item) => (
                    <div key={item.label} className="flex flex-col gap-1">
                      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">{item.label}</div>
                      <div className="text-gray-900 dark:text-white text-sm font-semibold capitalize">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next of Kin */}
              <div className="px-8 pb-6">
                <div className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/10 p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-green-700 dark:text-green-400 mb-3">
                    Emergency Contact
                  </p>
                  {nokName || nokPhone ? (
                    <div className="flex flex-col gap-2">
                      {nokName && (
                        <div className="flex flex-col gap-0.5">
                          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Name</div>
                          <div className="text-gray-900 dark:text-white text-sm font-semibold">{nokName}</div>
                        </div>
                      )}
                      {nokPhone && (
                        <div className="flex flex-col gap-0.5">
                          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Phone</div>
                          <div className="text-gray-900 dark:text-white text-sm font-semibold">{nokPhone}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No next of kin on record</p>
                  )}
                </div>
              </div>

              <div className="px-8 py-6 border-t border-gray-200 dark:border-white/10 flex flex-col gap-3">
                <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">Actions</div>
                <button
                  onClick={() => setCardModal(true)}
                  className="w-full border border-green-200 dark:border-green-800/50 hover:border-green-600 bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 hover:text-green-600 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                >
                  View Member Card
                </button>
                <button onClick={handleSendReminder} className="w-full border border-gray-200 dark:border-white/10 hover:border-green-600 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                  Send Subscription Reminder
                </button>
                <button onClick={handleRequestHeadshot} className="w-full border border-yellow-200 dark:border-yellow-900/50 hover:border-yellow-500 dark:hover:border-yellow-600 text-yellow-600 hover:text-yellow-500 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                  Request Headshot Update
                </button>
                <button
                  onClick={() => setDeactivateModal(true)}
                  className={`w-full border py-3 text-xs font-bold uppercase tracking-widest transition-colors ${member.status === 'active' ? 'border-orange-200 dark:border-orange-900/50 hover:border-orange-500 text-orange-500 hover:text-orange-400' : 'border-green-200 dark:border-green-900/50 hover:border-green-600 text-green-600 hover:text-green-500'}`}
                >
                  {member.status === 'active' ? 'Deactivate Member' : 'Activate Member'}
                </button>
                {member.role === 'supporter' && (
                  <button
                    onClick={() => setRoleModal(true)}
                    className="w-full border border-blue-200 dark:border-blue-800/50 hover:border-blue-500 bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 hover:text-blue-500 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                  >
                    Promote to Coordinator
                  </button>
                )}
                {member.role === 'chapter_coordinator' && (
                  <button
                    onClick={() => setRoleModal(true)}
                    className="w-full border border-blue-200 dark:border-blue-800/50 hover:border-blue-500 bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 hover:text-blue-500 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                  >
                    Demote to Supporter
                  </button>
                )}
                <button
                  onClick={() => setDeleteModal(true)}
                  className="w-full border border-red-300 dark:border-red-800/50 hover:border-red-600 bg-red-50 dark:bg-red-900/10 text-red-600 hover:text-red-500 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                >
                  Delete Member Permanently
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Deactivate Modal */}
      {deactivateModal && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 w-full max-w-sm p-8">
            <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800/50 flex items-center justify-center mx-auto mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-500">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h2 className="font-black text-lg uppercase tracking-tight text-center text-gray-900 dark:text-white mb-2">
              {member.status === 'active' ? 'Deactivate Member' : 'Activate Member'}
            </h2>
            <p className="text-gray-500 text-sm text-center leading-relaxed mb-6">
              {member.status === 'active'
                ? `This will revoke active membership for ${member.full_name}. Their card will show as inactive.`
                : `This will restore active membership for ${member.full_name}.`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeactivateModal(false)}
                className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateConfirm}
                disabled={actionLoading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-900 text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                {actionLoading ? 'Processing...' : member.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 w-full max-w-sm p-8">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 flex items-center justify-center mx-auto mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </div>
            <h2 className="font-black text-lg uppercase tracking-tight text-center text-gray-900 dark:text-white mb-2">
              Delete Member
            </h2>
            <p className="text-gray-500 text-sm text-center leading-relaxed mb-2">
              You are about to permanently delete
            </p>
            <p className="font-black text-center text-gray-900 dark:text-white uppercase tracking-tight mb-4">
              {member.full_name}
            </p>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3 mb-6">
              <p className="text-red-600 dark:text-red-400 text-xs leading-relaxed">
                This will permanently remove their account, subscription history and all associated data. <strong>This cannot be undone.</strong>
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(false)}
                className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={actionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                {actionLoading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {roleModal && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 w-full max-w-sm p-8">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 flex items-center justify-center mx-auto mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <h2 className="font-black text-lg uppercase tracking-tight text-center text-gray-900 dark:text-white mb-2">
              {member.role === 'supporter' ? 'Promote to Coordinator' : 'Demote to Supporter'}
            </h2>
            <p className="text-gray-500 text-sm text-center leading-relaxed mb-2">
              {member.role === 'supporter'
                ? `${member.full_name} will be able to log into the staff portal and manage their assigned chapter.`
                : `${member.full_name} will be moved back to a regular supporter account.`
              }
            </p>
            <p className="text-gray-400 text-xs text-center mb-6">
              They should use the {member.role === 'supporter' ? 'staff login at /staff/login' : 'supporter login at /login'} after this change.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setRoleModal(false)} className="flex-1 border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                Cancel
              </button>
              <button onClick={handlePromoteConfirm} disabled={promoting} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-900 text-white py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                {promoting ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member Card Modal */}
      {cardModal && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex flex-col items-center justify-center px-4">
          <div className="flex flex-col items-center gap-6 w-full">

            {/* Header */}
            <div className="flex items-center justify-between w-full max-w-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-widest">Membership Card</p>
                <p className="text-white font-black uppercase tracking-tight">{member.full_name}</p>
              </div>
              <button
                onClick={() => setCardModal(false)}
                className="text-gray-400 hover:text-white transition-colors text-xl"
              >
                ✕
              </button>
            </div>

            {/* Card — rendered at full scale */}
            <div className="transform scale-90 sm:scale-100 origin-top">
              <CardDisplay ref={cardRef} supporter={member} />
            </div>

            {/* Download button for admin */}
            <button
              onClick={async () => {
                if (!cardRef?.current) return
                try {
                  await toPng(cardRef.current, { pixelRatio: 2 })
                  const dataUrl = await toPng(cardRef.current, {
                    pixelRatio: 2,
                    backgroundColor: '#052e16',
                    cacheBust: true,
                  })
                  const link = document.createElement('a')
                  link.download = `${member.membership_id}-card.png`
                  link.href = dataUrl
                  link.click()
                } catch (err) {
                  console.error('Download failed:', err)
                }
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Download Card
            </button>

          </div>
        </div>
      )}
    </>
  )
}